import styled, { css } from "styled-components";

// The prototype puts every table on a card: hairline, 16px radius, faint shadow,
// clipped so the header's fill follows the corners. This carried none of it — no
// surface, no border, no cell padding — which is why tables read as loose text
// rather than as the prototype's panels. Rows sat apart on a row-gap too; they are
// flush now, separated by the divider the call sites already render.
export const PathwayTable = styled.div`
    display: grid;
    border: 1px solid var(--border-hairline);
    border-radius: var(--pw-radius-2xl);
    background: var(--surface-card);
    box-shadow: var(--pw-shadow-xs);
    overflow: hidden;

    /* Call sites render a divider after every row, including the last, which would
       otherwise draw a hairline hard against the card's own bottom edge. */
    & > *:last-child {
        border-bottom: none;
    }

    /* Some tables already sit inside a card or a modal body. They drop the chrome
       rather than nest a second panel inside the first. */
    ${(props) =>
        props.$flush &&
        css`
            border: none;
            border-radius: 0;
            background: none;
            box-shadow: none;
        `}
`;

// Drag-and-drop tables cannot use overflow:hidden because the transform-based
// drag animation gets clipped. Instead the corner clip is applied to the first
// and last row directly so the header and content backgrounds follow the radius
// without constraining the DnD transform.
export const PathwayDndTable = styled(PathwayTable)`
    overflow: visible;

    & > *:has(+ [role="status"]) > * {
        border-bottom: none;
    }

    ${(props) =>
        !props.$flush &&
        css`
            & > *:first-child {
                border-top-left-radius: calc(var(--pw-radius-2xl) - 1px);
                border-top-right-radius: calc(var(--pw-radius-2xl) - 1px);
                overflow: hidden;
            }

            & > *:has(+ [role="status"]) > * {
                border-bottom-left-radius: calc(var(--pw-radius-2xl) - 1px);
                border-bottom-right-radius: calc(var(--pw-radius-2xl) - 1px);
            }
        `}
`;

export const PathwayTableHeaderRow = styled.div`
    display: grid;
    align-items: center;
    padding: 11px 20px;
    border-bottom: 1px solid var(--border-hairline);
    background: var(--surface-table-head);
    column-gap: var(--gap-m);
    font-size: var(--pw-text-2xs);
    font-weight: var(--pw-weight-bold);
    letter-spacing: var(--pw-tracking-caps);
    text-transform: uppercase;
    color: var(--pw-neutral-400);
`;

const TableCell = styled.div`
    & > p {
        margin: 0;
        padding: 0;
    }
`;

export const PathwayTableContentRow = styled.div`
    display: grid;
    padding: 16px 20px;
    column-gap: var(--gap-m);
    align-items: start;
    font-size: var(--pw-text-base);
    transition: background var(--pw-transition-fast) var(--pw-ease);

    &:hover {
        background: var(--surface-table-row-hover);
    }

    /* Row actions rest at low contrast and come forward on hover. The reveal has to
       live on the row, since the actions cannot reach their own ancestor. */
    &:hover .pw-row-actions,
    &:focus-within .pw-row-actions {
        opacity: 1;
    }
`;

export const PathwayTableContentRowFontRegular = styled(PathwayTableContentRow)`
    font-weight: lighter;
`;

export const PathwayTableTwoCellsRow = styled(PathwayTableContentRow)`
    grid-template-columns: 50fr 50fr;
`;

export const PathwayTableHeader = styled(TableCell)`
    display: grid;
    font-weight: bold;
    align-content: center;
    align-self: center;
    row-gap: var(--gap-s);
`;

export const PathwayCenteredTableHeader = styled(PathwayTableHeader)`
    text-align: center;
`;

export const PathwayTableCellContent = styled(TableCell)`
    display: grid;
    /* A cell must never be wider than its column. Both zeros are needed: as a grid item the cell
       may not shrink below its own min-content without min-width, and min-content of nowrap content
       is the full string — so a long value pushes into the next column instead of truncating. */
    min-width: 0;
    grid-template-columns: minmax(0, 1fr);
    align-content: center;
    align-self: center;
    row-gap: var(--gap-s);
`;

export const PathwayCenteredTableCellContent = styled(PathwayTableCellContent)`
    text-align: center;
`;

export const PathwayLeftAlignTableCellContent = styled(PathwayTableCellContent)`
    text-align: left;
`;

export const TableContentDividerLine = styled.div`
    display: grid;
    border-bottom: 1px solid var(--border-hairline);
`;

export const TableHeaderLine = styled.div`
    border-bottom: 1px solid var(--border-hairline);
`;

export const AlignRightHeaderCell = styled(TableCell)`
    text-align: right;
    font-weight: bold;
`;

export const AlignLeftHeaderCell = styled(TableCell)`
    text-align: left;
    font-weight: bold;
`;

export const AlignRightCell = styled(TableCell)`
    text-align: right;
`;

export const AlignLeftCell = styled(TableCell)`
    text-align: left;
`;
